/**
 * Unified data model for the Org-Part Tree Builder.
 *
 * Both the Edit-Position dialog and the Bulk-Create-Position dialog now share
 * a single component (`OrgPartTreeBuilderComponent`) that operates on a
 * `BuilderTreeNode[]` tree. This file defines the node model, the per-launch
 * configuration contract, and the save-payload shape.
 *
 * Identity rule:
 *   - Every node's identity is `partKey`.
 *   - For existing (already-saved) Firestore parts, `partKey` is the real key
 *     and `isNew === false`.
 *   - For unsaved nodes added in this builder session, `partKey` is a synthesised
 *     temp key (e.g. `new-{ts}-{n}`) and `isNew === true`. The backend resolves
 *     these to real keys atomically inside `bulkSave`.
 *
 * This unification eliminates the historical mismatch between the two dialogs
 * (edit used `partKey` directly; bulk wrapped everything in a `tempId` plus a
 * sibling `existingPartKey`).
 */

import {
    BulkPartNode,
    BulkGraftPoint,
    BulkSaveMove,
    BulkSaveUpdate,
    BulkSaveReorder,
    Organization,
    OrgPart,
} from '@newgenus/common';

// ─────────────────────────────────────────────────────────────────────────────
// BuilderTreeNode — the unified UI node model
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A single node in the builder tree. Both dialogs convert their domain inputs
 * into `BuilderTreeNode[]` on init and feed it as the `initialTree` input.
 */
export interface BuilderTreeNode {
    // ── Identity ────────────────────────────────────────────────────────
    /** Real Firestore partKey for existing nodes, synthesised temp key for new nodes. */
    partKey: string;
    /** True when this node has never been written to Firestore. */
    isNew: boolean;

    // ── Editable fields ─────────────────────────────────────────────────
    name: string;
    subTitle: string;
    partType: string;
    colour: { background: string; color: string; linkColor: string };
    vfp: string;
    /** Optional header-strip role for posts (head/deputy/assistant). UI widens
     *  the strict type to include the empty string used by the picker for "Body". */
    headerRole?: 'head' | 'deputy' | 'assistant' | '';
    /** Detached post group (e.g. 'board' = the Board of Directors box). Empty
     *  string = normal on-board placement. Posts only. */
    detachedGroup?: string;
    /** partKey of the division the detached box draws its cosmetic connector to. */
    detachedGroupConnectsTo?: string;
    /** True = this post does not cascade Held-From-Above (governance seats). */
    excludeFromHfa?: boolean;
    /** Linked post: partKey of the post whose holders this one shows (org-board/linked-posts.ts).
     *  Empty string = not linked. Posts only. */
    linkedPartKey?: string;

    // ── Hierarchy ───────────────────────────────────────────────────────
    children: BuilderTreeNode[];
    /** Real Firestore parent key for existing nodes; for new nodes can be a
     *  real key (graft point) or a temp parent key (nested under another new node). */
    parentPartKey: string;
    /** Snapshot of `parentPartKey` at load time — used to detect cross-parent
     *  moves at save time. Only meaningful when `isNew === false`. */
    _originalParentPartKey?: string;
    /** Snapshot of sibling index at load time — used so a drag that returns
     *  to the original parent + index clears the `moved` flag. */
    _originalIndex?: number;

    // ── Optional metadata (from OrgPart) ────────────────────────────────
    /** Tree depth (only meaningful for existing nodes; otherwise inferred). */
    depth?: number;
    /** Sibling sequence id (legacy; preserved when round-tripping OrgParts). */
    id?: number;
    /** Path string (legacy; preserved when round-tripping OrgParts). */
    path?: string;

    // ── UI state ────────────────────────────────────────────────────────
    collapsed: boolean;
    /** Indentation level in flat display. Recomputed by the builder on the fly. */
    level: number;
    dirty: boolean;
    moved: boolean;

    // ── Dirty-tracking snapshot ─────────────────────────────────────────
    _original: {
        name: string;
        subTitle: string;
        partType: string;
        colour: { background: string; color: string; linkColor: string };
        vfp?: string;
        headerRole?: 'head' | 'deputy' | 'assistant' | '';
        detachedGroup?: string;
        detachedGroupConnectsTo?: string;
        excludeFromHfa?: boolean;
        linkedPartKey?: string;
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// BuilderConfig — per-launch configuration contract
// ─────────────────────────────────────────────────────────────────────────────

/** Provider for allowed child types of a given parent + level. */
export type AllowedChildTypesFn = (parentType: string, level: number) => string[];

/** Optional pre-save hook returning false to abort the save. */
export type PreSaveHook = () => Promise<boolean>;

/** Async refresh callback used by Continue Editing — wrappers re-fetch their
 *  domain data (organization, anchor, descendants) and produce a fresh tree. */
export type RefreshTreeFn = (collapsedKeys: Map<string, boolean>) => Promise<{
    initialTree: BuilderTreeNode[];
    breadcrumb?: BuilderConfig['breadcrumb'];
} | null>;

/** Save command — wrapper supplies the `obService.bulkSave(orgKey, ...)` closure.
 *  The current in-memory tree is also passed (read-only) so wrappers that don't
 *  use the diff payload (e.g. the org-creation wizard's Step 3 adapter) can
 *  flatten the tree directly into their own state. Existing dialog wrappers
 *  ignore the second argument. */
export type SaveCommandFn = (payload: BuilderSavePayload, tree?: readonly BuilderTreeNode[]) => Promise<void>;

export interface BuilderConfig {
    /** Dialog mode. Drives single-vs-multi-root semantics, MAX_POSITIONS,
     *  Add-Root button, and label wording. */
    mode: 'edit' | 'bulk';

    /** Minimum drop depth.
     *  edit = 1 (cannot escape the root); bulk = 0 (multiple roots allowed). */
    minDepth: 0 | 1;

    /** When set, the matching node cannot be dragged. Edit dialog supplies its
     *  rootNode.partKey so the root part is locked in place. */
    protectedRootPartKey?: string;

    /** Open with one new Board of Directors seat already added under the root
     *  (the board box's "Add board seat" action). `connectsTo` = the division the
     *  box's line already points at, so the new seat keeps the same line. */
    seedBoardSeat?: { connectsTo?: string | null };

    /** True for the bulk dialog — shows the "Add Root" button in the header. */
    showAddRootButton: boolean;

    /** Hard cap on new-node count. Bulk = 50, Edit = unlimited (omit). */
    maxPositions?: number;

    /** Allowed child-type provider. Edit passes a simple parent→children map;
     *  bulk passes the level-aware variant (2-tier executive support). */
    getAllowedChildTypes: AllowedChildTypesFn;

    /** Default colour palette for new nodes (bulk inherits this from anchor). */
    defaultColour: { background: string; color: string; linkColor: string };

    /** Hide the subtitle field for new (unsaved) nodes. Bulk = true (subtitle
     *  is reserved for already-saved parts). Edit = false. */
    hideSubtitleForNewNodes: boolean;

    /** Anchor partKey — bulk dialog supplies this; edit dialog supplies its
     *  rootNode.partKey. The save payload's `add.anchorPartKey` uses this. */
    anchorPartKey: string;

    /** How new root-level nodes attach to the anchor. Edit = 'child' (root is
     *  always a container). Bulk = whatever the parent dialog passed in. */
    insertionType: 'child' | 'sibling';

    /** Breadcrumb to render. Edit derives this from the org tree; bulk passes
     *  it in. The shared component just renders. */
    breadcrumb: { partKey?: string; name: string; partType: string }[];

    /** When true, a "(adding under <anchor>)" chip is appended after the breadcrumb. */
    showInsertionPointChip: boolean;

    /** Strap line displayed beside the title. Edit shows "<n> descendants";
     *  bulk shows "adding under <anchorName>". */
    headerSubtitle: string;

    /** Material icon for the header. Default 'edit' for edit, 'playlist_add' for bulk. */
    headerIcon: string;

    /** Header title (e.g. "Edit Position", "Bulk Create Positions"). */
    headerTitle: string;

    /** Footer save button label ("Apply Changes" vs "Create"). */
    saveButtonLabel: string;

    /** Show the developer-info footer (edit-only when launched by a developer). */
    showDeveloperInfo: boolean;

    /** Organization (used for resolving part names in the task monitor). */
    organization: Organization;

    /** Optional pre-save hook (e.g. edit's post→container warning). Returning
     *  false aborts the save without entering the writing/processing phase. */
    preSave?: PreSaveHook;

    /** Save command — wrapper supplies a closure over `obService.bulkSave(orgKey, ...)`. */
    saveCommand: SaveCommandFn;

    /** Continue-editing data refresh — wrapper rebuilds the initial tree
     *  from fresh domain data and returns it. Optional: when omitted, the
     *  Continue Editing button is hidden. */
    refreshTree?: RefreshTreeFn;

    /**
     * When true, `onSave()` skips the Firestore-task monitor entirely and
     * jumps from `writing` → `done` as soon as `saveCommand` resolves.
     *
     * Used by the org-creation wizard's Step 3 wrapper, where the "save" is a
     * synchronous in-memory mutation of the OrgDraft (no `securityTasks/` rows
     * to wait for). Defaults to false (full monitor flow) for backwards
     * compatibility with the edit / bulk-create dialogs.
     */
    skipTaskMonitor?: boolean;

    /**
     * When true, the builder hides its own Cancel / Save footer + the
     * right-hand "Pending Changes" panel and instead auto-flushes every
     * mutation back to the wrapper via `saveCommand` (debounced ~500 ms).
     *
     * Used by the org-creation wizard's Step 3 — every edit becomes an
     * entry in the wizard's own undo/redo history, and the wizard's
     * top-bar Undo/Redo buttons govern reverting structure changes.
     */
    autoSave?: boolean;

    /**
     * Optional row-level crown UI for marking the org's CEO post.
     * When BOTH hooks are supplied, the builder renders a crown button on
     * every row whose `partType === 'post'`. Clicking it calls
     * `onTogglePostCrown(partKey)`; the button's highlighted state is read
     * from `isPostCrowned(partKey)`. The crown lives outside the builder's
     * own dirty/move/delete bookkeeping — it's a wizard-level concept and
     * the wrapper owns the underlying state. Plan addendum 19.
     */
    isPostCrowned?: (partKey: string) => boolean;
    onTogglePostCrown?: (partKey: string) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// BuilderSavePayload — exactly what `OrgBoardService.bulkSave(...)` accepts
// ─────────────────────────────────────────────────────────────────────────────

export interface BuilderSavePayload {
    updates: BulkSaveUpdate[];
    moves: BulkSaveMove[];
    add: {
        anchorPartKey: string;
        insertionType: 'child' | 'sibling';
        tree: BulkPartNode[];
        totalCount: number;
        siblingOrder?: string[];
        graftPoints?: BulkGraftPoint[];
    } | null;
    reorders: BulkSaveReorder[];
    deletes: string[];
    /** New-post count — used by the task monitor to wait for matching addHat tasks. */
    expectedNewHatCount: number;
    /** True when the save is purely a reorder (no new/edit/delete/move).
     *  The task monitor uses this to relabel `bulkSave` / `bulkAddParts`
     *  task icons as "moved" instead of "creating". */
    reorderOnlySave: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Adapter helpers — convert domain types into `BuilderTreeNode`
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a `BuilderTreeNode` from an existing `OrgPart` plus its descendants
 * found in `allOrgParts`. Recursively walks down. Used by the edit dialog
 * (single-root subtree) and by the bulk dialog (per existing reference root).
 */
export function buildBuilderNodeFromOrgPart(
    part: OrgPart,
    allOrgParts: OrgPart[],
    level: number,
    extractIndex: (path: string) => number,
    defaultColour: { background: string; color: string; linkColor: string },
    rootCollapsed = true
): BuilderTreeNode {
    const sortedSiblings = allOrgParts
        .filter((p) => p.parentPartKey === part.parentPartKey)
        .sort((a, b) => extractIndex(a.path) - extractIndex(b.path));
    const originalIndex = sortedSiblings.findIndex((p) => p.partKey === part.partKey);

    const children = allOrgParts
        .filter((p) => p.parentPartKey === part.partKey)
        .sort((a, b) => extractIndex(a.path) - extractIndex(b.path))
        .map((c) => buildBuilderNodeFromOrgPart(c, allOrgParts, level + 1, extractIndex, defaultColour));

    const colour = {
        background: part.colour?.background || defaultColour.background,
        color: part.colour?.color || defaultColour.color,
        linkColor: part.colour?.linkColor || defaultColour.linkColor,
    };

    return {
        partKey: part.partKey,
        isNew: false,
        name: part.name || '',
        subTitle: part.subTitle || '',
        partType: part.partType || '',
        colour,
        vfp: part.vfp || '',
        headerRole: part.headerRole || '',
        detachedGroup: (part as any).detachedGroup || '',
        detachedGroupConnectsTo: (part as any).detachedGroupConnectsTo || '',
        excludeFromHfa: (part as any).excludeFromHfa === true,
        linkedPartKey: (part as any).linkedPartKey || '',
        children,
        parentPartKey: part.parentPartKey || '',
        _originalParentPartKey: part.parentPartKey || '',
        _originalIndex: originalIndex >= 0 ? originalIndex : 0,
        depth: part.depth,
        id: part.id,
        path: part.path,
        collapsed: rootCollapsed,
        level,
        dirty: false,
        moved: false,
        _original: {
            name: part.name || '',
            subTitle: part.subTitle || '',
            partType: part.partType || '',
            colour: { ...colour },
            vfp: part.vfp || '',
            headerRole: part.headerRole || '',
            detachedGroup: (part as any).detachedGroup || '',
            detachedGroupConnectsTo: (part as any).detachedGroupConnectsTo || '',
            excludeFromHfa: (part as any).excludeFromHfa === true,
            linkedPartKey: (part as any).linkedPartKey || '',
        },
    };
}

/**
 * Convert a `BuilderTreeNode` (a NEW node) into the strict `BulkPartNode`
 * shape the backend `bulkSave` handler expects. Recursively converts only
 * NEW children — existing children belong in graft-point sibling lists.
 *
 * The node's temp `partKey` is used directly as the `tempId` — the backend
 * resolves these to real Firestore keys atomically.
 */
export function builderNodeToBulkPartNode(node: BuilderTreeNode): BulkPartNode {
    const out: BulkPartNode = {
        tempId: node.partKey,
        name: node.name || 'Unnamed',
        partType: node.partType,
        colour: { ...node.colour },
        vfp: node.vfp,
        children: node.children.filter((c) => c.isNew).map((c) => builderNodeToBulkPartNode(c)),
    };
    if (node.partType === 'post' && node.headerRole) {
        out.headerRole = node.headerRole as 'head' | 'deputy' | 'assistant';
    }
    // Detached-group placement (e.g. Board of Directors box) for new posts.
    if (node.partType === 'post' && node.detachedGroup) {
        out.detachedGroup = node.detachedGroup;
        if (node.detachedGroupConnectsTo) out.detachedGroupConnectsTo = node.detachedGroupConnectsTo;
        if (node.excludeFromHfa === true) out.excludeFromHfa = true;
    }
    // Linked post (shows another post's holders) — posts only.
    if (node.partType === 'post' && node.linkedPartKey) {
        out.linkedPartKey = node.linkedPartKey;
    }
    return out;
}

/**
 * Extract the trailing numeric index from an OrgPart `path` string like
 * `children.2.children.0.data` → 0. Standalone helper because both wrappers
 * need it when adapting their existing OrgPart inputs into BuilderTreeNodes.
 */
export function extractIndexFromPath(path: string): number {
    if (!path) return 0;
    const parts = path.split('.');
    for (let i = parts.length - 1; i >= 0; i--) {
        if (parts[i] === 'data' && i > 0 && parts[i - 1].match(/^\d+$/)) {
            return parseInt(parts[i - 1], 10);
        }
    }
    return 0;
}
